import type { Dispatch, Middleware, UnknownAction } from '@reduxjs/toolkit';
import escapeStrRegexp from 'escape-string-regexp';
import type { RootState } from '../reducers';

export interface FxProcessContext<
  Deps = Record<string, unknown>,
  Action extends UnknownAction = UnknownAction,
> {
  action: Action;
  dispatch: Dispatch;
  previousState: RootState;
  getState: () => RootState;
  deps: Deps;
}

export type FxProcess<
  Deps = Record<string, unknown>,
  Action extends UnknownAction = UnknownAction,
> = (ctx: FxProcessContext<Deps, Action>) => void;

export interface Fx<Deps = Record<string, unknown>> {
  type: string;
  process: FxProcess<Deps>;
}

export function matchesType(
  fx: { type: string },
  action: UnknownAction
): boolean {
  // when fx type string contains * treat it like a glob pattern
  if (fx.type.includes('*')) {
    const escapedFxType = escapeStrRegexp(fx.type);
    const fxTypeStrRegexp = escapedFxType.replace(/\\\*/g, '.*?');
    const fxTypeRegexp = new RegExp(fxTypeStrRegexp);

    return fxTypeRegexp.test(action.type);
  }

  // otherwise test for strict equality
  return fx.type === action.type;
}

export function createFxMiddleware<Deps = Record<string, unknown>>(
  fxList: Fx<Deps>[],
  deps: Deps = {} as Deps
): Middleware {
  return ({ dispatch, getState }) =>
    (next) =>
    (action) => {
      const previousState = getState() as RootState;
      const result = next(action);
      const unknownAction = action as UnknownAction;
      const relatedFxList = fxList.filter((fx) =>
        matchesType(fx, unknownAction)
      );
      relatedFxList.forEach((fx) =>
        fx.process({
          action: unknownAction,
          dispatch: dispatch as Dispatch,
          previousState,
          getState: getState as () => RootState,
          deps,
        })
      );
      return result;
    };
}

export function createFx<
  Deps = Record<string, unknown>,
  Action extends UnknownAction = UnknownAction,
>(
  actionType: { toString(): string } | string,
  process: FxProcess<Deps, Action>
): Fx<Deps> {
  if (typeof process !== 'function') {
    throw new Error('param process is not a function');
  }

  return {
    type: actionType.toString(),
    // Wrap so the stored process is FxProcess<Deps> (UnknownAction).
    // The action cast is safe: matchesType has already confirmed action.type matches before calling process.
    process: (ctx) => process({ ...ctx, action: ctx.action as Action }),
  };
}
